import { CoachAthleteSearch } from '../../components/CoachAthleteSearch';
import { normalizeAthleteSearch } from '../../utils/athleteSearch';
import '../../styles/dashboard-annual-system.css';
import { ActionButton, ActionIconButton } from '../../components/ui/ActionButton';
import {sortAthletesByWorkout} from '../../utils/sortAthletesByWorkout';
import { displayDigits } from '../../utils/displayDigits';
import { AthleteOverviewPanel } from '../../components/AthleteOverviewPanel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoCalendarOutline, IoPeopleOutline, IoSettingsOutline, IoTrashOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { AddAthleteModal } from '../../components/AddAthleteModal';
import { AthleteSettingsModal } from '../../components/athlete-settings/AthleteSettingsModal';
import { CoachAssistantModal } from '../../components/coach/CoachAssistantModal';
import { NmCircularScore } from '../../components/NmCircularScore';
import { NmCard } from '../../components/NmCard';
import { NmEmptyState } from '../../components/NmEmptyState';
import { NmSectionHeader } from '../../components/NmSectionHeader';
import { NmSkeleton } from '../../components/NmSkeleton';
import { useAthleteContext } from '../../context/AthleteContext';
import { useAuth } from '../../hooks/useAuth';
import {
  coachAthlete,
  home,
  type AthleteItem,
  type HomeData,
} from '../../services/api';
import { toastErrorMessage, useToast } from '../../context/ToastContext';
import { useCoachDashboardOverview } from '../../hooks/useCoachDashboardOverview';
import { AthleteHomeView } from './AthleteHomeView';
import { DashboardAiExperience } from '../../components/ai/DashboardAiExperience';
import type { PageId } from '../../components/Sidebar';

function tsbVariant(tsb: number | null): 'success' | 'warning' | 'danger' | 'neutral' {
  if (tsb == null) return 'neutral';
  if (tsb < 0) return 'danger';
  if (tsb > 5) return 'success';
  return 'warning';
}

interface Props {
  athletes: AthleteItem[];
  refreshKey?: number;
}

export function CoachHomeView({ athletes, refreshKey = 0 }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { refreshProfile, user } = useAuth();
  const { selectAthlete } = useAthleteContext();
  const [showAdd, setShowAdd] = useState(false);
  const [assistantAthlete, setAssistantAthlete] = useState<AthleteItem | null>(null);
  const [settingsAthlete, setSettingsAthlete] = useState<AthleteItem | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [athleteQuery, setAthleteQuery] = useState('');
  const showAthleteSearch = athletes.length > 10;
  const searchQuery = showAthleteSearch ? normalizeAthleteSearch(athleteQuery) : '';
  const visibleAthletes = sortAthletesByWorkout(athletes).filter((athlete) =>
    !searchQuery || normalizeAthleteSearch(athlete.full_name ?? '').includes(searchQuery),
  );
  const [details, setDetails] = useState<Record<number, HomeData>>({});
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const { data: overview } = useCoachDashboardOverview(refreshKey);
  const overviewById = useMemo(
    () => new Map((overview?.athletes ?? []).map((athlete) => [athlete.id, athlete])),
    [overview?.athletes],
  );

  const loadDetail = useCallback(async (id: number) => {
    setLoadingId(id);
    try {
      const res = await home.getHomeData(id);
      setDetails((prev) => ({ ...prev, [id]: res.data?.data ?? ({} as HomeData) }));
    } catch {
      setDetails((prev) => ({ ...prev, [id]: {} as HomeData }));
    } finally {
      setLoadingId(null);
    }
  }, []);

  useEffect(() => {
    if (expandedId != null && !details[expandedId]) {
      void loadDetail(expandedId);
    }
  }, [expandedId, details, loadDetail]);

  async function toggleExpand(athlete: AthleteItem) {
    const id = athlete.id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!details[id]) await loadDetail(id);
  }

  async function removeAthlete(athlete: AthleteItem) {
    const confirmed = window.confirm(
      t('app.removeAthlete.message', { name: athlete.full_name }),
    );
    if (!confirmed) return;

    setRemovingId(athlete.id);
    try {
      await coachAthlete.remove(athlete.id);
      setExpandedId((current) => (current === athlete.id ? null : current));
      setDetails((current) => {
        const next = { ...current };
        delete next[athlete.id];
        return next;
      });
      await refreshProfile();
      toast.success(t('toast.deleted'));
    } catch (error: unknown) {
      toast.error(toastErrorMessage(error, t('app.removeAthleteFailed')));
    } finally {
      setRemovingId(null);
    }
  }

  const navigateFromAi = (page: PageId) => {
    if (page === 'charts') navigate('/charts');
    else if (page === 'calendar') navigate('/calendar');
    else navigate('/home');
  };

  return (
    <div>
      <DashboardAiExperience
        mode="coach"
        viewerUserId={user?.id ?? null}
        athletes={athletes}
        refreshKey={refreshKey}
        onNavigate={navigateFromAi}
        onSelectAthlete={selectAthlete}
        onOpenWorkout={(workoutId, date, athleteId) => {
          if (athleteId) selectAthlete(athleteId);
          const params = new URLSearchParams({ workoutId: String(workoutId) });
          if (date) params.set('date', date);
          navigate(`/calendar?${params.toString()}`);
        }}
        coachOverview={overview}
      />

      <NmSectionHeader
        className="coach-athlete-section-header"
        title={t('home.coach.title', 'My athletes')}
        count={athletes.length}
        actionLabel={t('app.addAthlete.title', 'Add athlete')}
        onAction={() => setShowAdd(true)}
      />

      {!athletes.length && (
        <NmEmptyState actionLabel={t('app.addAthlete.title', 'Add athlete')} onAction={() => setShowAdd(true)}>
          {t('coach.addAthleteFirst')}
        </NmEmptyState>
      )}

      {athletes.length > 0 && <>
        {showAthleteSearch && <CoachAthleteSearch value={athleteQuery} onChange={setAthleteQuery} />}
        <div className="coach-athlete-list">
            {visibleAthletes.length === 0 && <p className="coach-athlete-search-empty" role="status">{t('athletePicker.noResults')}</p>}
      {visibleAthletes.map((a) => {
        const snapshot = overviewById.get(a.id);
        const tsbFactor = snapshot?.readiness?.factors?.tsb;
        const rawTsb = snapshot?.training_load?.tsb
          ?? (tsbFactor?.available ? tsbFactor.value : null)
          ?? details[a.id]?.today_trends?.tsb;
        const tsb = rawTsb != null && Number.isFinite(Number(rawTsb))
          ? Math.round(Number(rawTsb))
          : null;
        const variant = tsbVariant(tsb);
        const expanded = expandedId === a.id;
        const isShared = a.coaching_relationship === 'assistant';
        return (
          <div key={a.id} className={`coach-athlete-card coach-athlete-row${expanded ? ' coach-athlete-card--expanded' : ''}`}>
            <div className="coach-athlete-header">
              <button
                type="button"
                className="coach-athlete-header__main"
                aria-expanded={expanded}
                onClick={() => void toggleExpand(a)}
              >
                <div className="coach-athlete-header__avatar">
                  {a.profile_image ? (
                    <img src={a.profile_image} alt="" />
                  ) : (
                    <span>{(a.full_name ?? '?').charAt(0)}</span>
                  )}
                </div>
                <div className="coach-athlete-header__info">
                  <div className="coach-athlete-header__name">{a.full_name}</div>
                  {isShared && <span className="coach-athlete-header__shared">{t('athleteRoster.shared')}</span>}
                </div>
              </button>
              <div className="coach-athlete-actions coach-athlete-actions--header">
                <ActionIconButton
                  type="button"
                  className="lib-icon-btn lib-icon-btn--xs lib-icon-btn--primary coach-athlete-actions__btn"
                  title={t('home.coach.openCalendar', 'Calendar')}
                  onClick={() => {
                    selectAthlete(a.id);
                    navigate('/calendar');
                  }}
                  label={t('home.coach.openCalendar', 'Calendar')}
                  icon={<IoCalendarOutline size={16} aria-hidden />}
                />
              </div>
              <button
                type="button"
                className="coach-athlete-header__tag"
                onClick={() => void toggleExpand(a)}
                aria-label={
                  tsb != null
                    ? `${i18n.resolvedLanguage?.startsWith('fa') ? 'فرم (TSB)' : t('readiness.tsb', 'Battery (TSB)')} ${displayDigits(tsb)}`
                    : t('home.coach.tapToView', 'Tap to view home')
                }
              >
                <NmCircularScore
                  value={tsb == null ? null : Math.min(100, Math.abs(tsb))}
                  displayValue={tsb ?? undefined}
                  unit=""
                  variant={variant}
                  direction={tsb != null && tsb < 0 ? 'counterclockwise' : 'clockwise'}
                  size={44}
                />
              </button>
            </div>

            {expanded && (
              <AthleteOverviewPanel>
                {!isShared && (
                  <div className="coach-athlete-manage-row">
                    <ActionButton
                      type="button"
                      className="coach-athlete-manage-row__button"
                      onClick={() => setSettingsAthlete(a)}
                      wrap
                    ><IoSettingsOutline size={17} aria-hidden />
                      <span>{t('app.athleteSettings')}</span></ActionButton>
                    <ActionButton
                      type="button"
                      className="coach-athlete-manage-row__button"
                      onClick={() => setAssistantAthlete(a)}
                      wrap
                    ><IoPeopleOutline size={17} aria-hidden />
                      <span>{t('home.coach.assistantCoaches')}</span></ActionButton>
                    <ActionButton
                      type="button"
                      className="coach-athlete-manage-row__button coach-athlete-manage-row__button--danger"
                      disabled={removingId === a.id}
                      aria-busy={removingId === a.id}
                      onClick={() => void removeAthlete(a)}
                      wrap
                      variant="danger"
                    ><IoTrashOutline size={17} aria-hidden />
                      <span>{t('home.coach.remove')}</span></ActionButton>
                  </div>
                )}
                {loadingId === a.id ? (
                  <NmSkeleton height={120} />
                ) : details[a.id] ? (
                  <AthleteHomeView
                    athleteId={a.id}
                    data={details[a.id]}
                    showAiBrief={false}
                  />
                ) : null}
              </AthleteOverviewPanel>
            )}
          </div>
        );
      })}
      </div></>}

      {showAdd && (
        <AddAthleteModal
          onClose={() => setShowAdd(false)}
          onSent={() => {
            setShowAdd(false);
            void refreshProfile();
          }}
        />
      )}

      {assistantAthlete && (
        <CoachAssistantModal
          athlete={assistantAthlete}
          onClose={() => setAssistantAthlete(null)}
        />
      )}

      {settingsAthlete && (
        <AthleteSettingsModal
          athleteId={settingsAthlete.id}
          onClose={() => setSettingsAthlete(null)}
          onSaved={() => {
            void refreshProfile();
            void loadDetail(settingsAthlete.id);
          }}
        />
      )}
    </div>
  );
}
